import type { WarehouseSourceSwitchKey } from './model/types';

interface SourceOption {
    key: WarehouseSourceSwitchKey;
    available: boolean;
    label: string;
}

interface SourceSwitchesProps {
    sourceOptions: SourceOption[];
    switchLabels: Record<WarehouseSourceSwitchKey, string>;
    switchHints: Record<WarehouseSourceSwitchKey, string>;
    useReserve: boolean;
    useTeam: boolean;
    useCustom: boolean;
    useMain: boolean;
    addServices: boolean;
    skipExisting: boolean;
    hasAdditionalItems: boolean;
    disabled?: boolean;
    switchServicesHint: string;
    switchServicesLabel: string;
    switchSkipExistingLabel: string;
    switchSkipExistingHint: string;
    sourcesTitle: string;
    onToggleReserve: (value: boolean) => void;
    onToggleTeam: (value: boolean) => void;
    onToggleCustom: (value: boolean) => void;
    onToggleMain: (value: boolean) => void;
    onToggleServices: (value: boolean) => void;
    onToggleSkipExisting: (value: boolean) => void;
}

const SourceSwitches = ({
    sourceOptions,
    switchLabels,
    switchHints,
    useReserve,
    useTeam,
    useCustom,
    useMain,
    addServices,
    skipExisting,
    hasAdditionalItems,
    disabled,
    switchServicesHint,
    switchServicesLabel,
    switchSkipExistingLabel,
    switchSkipExistingHint,
    sourcesTitle,
    onToggleReserve,
    onToggleTeam,
    onToggleCustom,
    onToggleMain,
    onToggleServices,
    onToggleSkipExisting,
}: SourceSwitchesProps) => {
    const valueByKey = {
        reserve: useReserve,
        team: useTeam,
        custom: useCustom,
        main: useMain,
    };
    const onToggleByKey = {
        reserve: onToggleReserve,
        team: onToggleTeam,
        custom: onToggleCustom,
        main: onToggleMain,
    };

    return (
        <div className="mx-3 mb-3 mt-3">
            <h6 className="text-muted mb-3">{sourcesTitle}</h6>
            <div className="d-flex flex-column gap-3">
                {sourceOptions
                    .filter((opt) => opt.available)
                    .map((opt) => (
                        <div key={opt.key} className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`p2w-source-${opt.key}`}
                                checked={Boolean(valueByKey[opt.key])}
                                disabled={disabled}
                                onChange={(e) => onToggleByKey[opt.key](e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`p2w-source-${opt.key}`}>
                                {switchLabels[opt.key]}
                            </label>
                            <div className="small text-muted">{switchHints[opt.key]}</div>
                        </div>
                    ))}
            </div>

            {hasAdditionalItems ? (
                <div className="mt-4 pt-3 border-top">
                    <div className="form-check form-switch mb-0">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="p2w-add-services"
                            checked={addServices}
                            disabled={disabled}
                            onChange={(e) => onToggleServices(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="p2w-add-services">
                            {switchServicesLabel}
                        </label>
                        <div className="small text-muted">{switchServicesHint}</div>
                    </div>
                </div>
            ) : null}

            <div className="mt-4 pt-3 border-top">
                <div className="form-check form-switch mb-0">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="p2w-skip-existing"
                        checked={skipExisting}
                        disabled={disabled}
                        onChange={(e) => onToggleSkipExisting(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="p2w-skip-existing">
                        {switchSkipExistingLabel}
                    </label>
                    <div className="small text-muted">{switchSkipExistingHint}</div>
                </div>
            </div>
        </div>
    );
};

export default SourceSwitches;
